import React, { useState, useEffect, useMemo, useRef } from 'react';
import { collection, query, where, getDocs, Timestamp, doc, getDoc, updateDoc, serverTimestamp, addDoc, setDoc } from 'firebase/firestore';
import { 
  Box, 
  Tabs, 
  Tab, 
  Typography, 
  CircularProgress,
  ThemeProvider,
  createTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { db } from '../firebase';
import { LINE_STATUSES, ORDER_STATUSES } from '../firebase/schema';
import { syncOrderPaymentIfInvoiceable } from '../utils/orderPayment';
import { calculateOptimalPrintlist } from '../utils/printListCalculations';
import { activityLogAppend } from '../utils/lineActivityLog';
import { reasonSpLineCannotPrint } from '../utils/spLicenseStore';
import { formatDate } from '../utils/dateFormatter';
import QCPrintedModal from '../components/QCPrintedModal';
import PrintLineModal from '../components/PrintLineModal';
import BoxCountDialog from '../components/BoxCountDialog';
import BoxSticker from '../components/BoxSticker';
import { differenceInCalendarDays, startOfDay, isWeekend } from 'date-fns';
import PrintlistSuccessAnimation from '../components/PrintlistSuccessAnimation';
import styled from '@emotion/styled';
import { useFacility } from '../context/FacilityContext';
import { filterByFacility, facilityProgressDocId } from '../utils/facilities';
import { printLabelBase64 } from '../utils/printServer';

// Create a dark theme that matches our global styles
const theme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#1C1C1C',
      paper: '#2C2C2C'
    },
    primary: {
      main: '#98FB98'
    },
    text: {
      primary: '#FFFFFF',
      secondary: 'rgba(255, 255, 255, 0.7)'
    }
  },
  typography: {
    fontFamily: "'Montserrat', sans-serif",
    button: {
      textTransform: 'uppercase'
    }
  },
  components: {
    MuiTableContainer: {
      styleOverrides: {
        root: {
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          boxSizing: 'border-box'
        }
      }
    },
    MuiTable: {
      styleOverrides: {
        root: {
          width: '100%',
          maxWidth: '100%',
          tableLayout: 'fixed',
          boxSizing: 'border-box'
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(255, 255, 255, 0.1)',
          padding: '6px 8px',
          fontSize: '0.78rem',
          textTransform: 'uppercase'
        },
        head: {
          fontWeight: 600,
          backgroundColor: '#1C1C1C',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          fontSize: '0.7rem'
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          backgroundColor: '#2C2C2C',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          width: '100%'
        },
        indicator: {
          backgroundColor: '#98FB98'
        },
        scrollButtons: {
          color: '#98FB98',
          '&.Mui-disabled': { opacity: 0.25 }
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'uppercase',
          fontSize: '0.85rem',
          fontWeight: 600,
          '&.Mui-selected': {
            color: '#98FB98'
          },
          '@media (max-width: 768px)': {
            flex: 1,
            minWidth: 'auto',
            padding: '12px 8px',
            fontSize: '0.8rem'
          }
        }
      }
    }
  }
});

const DAILY_MINUTES = 420; // 7 hours

/** Match OrderView totals for box sticker (order doc + all lines). */
const computeBoxStickerTotals = (order, lines) => {
  const safeLines = lines || [];
  const totalPieces = safeLines.reduce((sum, line) => {
    const lineQty = Object.values(line.sizes || {}).reduce((s, qty) => s + (Number(qty) || 0), 0);
    return sum + lineQty;
  }, 0);
  const subtotal = safeLines.reduce((sum, line) => {
    const lineQty =
      line.totalQuantity ||
      Object.values(line.sizes || {}).reduce((s, qty) => s + (Number(qty) || 0), 0);
    return sum + lineQty * (parseFloat(line.pricePerPiece) || 0);
  }, 0);
  let discountAmount = 0;
  if (order?.discount && order.discount.amount) {
    if (order.discount.type === 'percent') {
      discountAmount = subtotal * (parseFloat(order.discount.amount) / 100);
    } else {
      discountAmount = parseFloat(order.discount.amount) || 0;
    }
  }
  const taxableAmount = subtotal - discountAmount;
  const isTaxExempt =
    order?.taxExempt === true || order?.additionalInstructions?.taxExempt === true;
  const tax = isTaxExempt ? 0 : taxableAmount * 0.07;
  const total = subtotal - discountAmount + tax;
  return { totalPieces, total };
};

// Helper functions
const isDTGJob = (line) => {
  // Check the line type directly, matching Firestore's lowercase value
  const result = line?.type === 'dtg';
  console.log('isDTGJob check:', {
    lineId: line?.id,
    type: line?.type,
    isDTG: result
  });
  return result;
};

const isOverdue = (job, today) => {
  if (!job?.dueDate) return false;
  const due = startOfDay(new Date(job.dueDate));
  return due < today;
};

// Helper to split jobs by due date
const splitJobsByDay = (jobs) => {
  const today = startOfDay(new Date());
  const tomorrow = startOfDay(new Date(Date.now() + 24 * 60 * 60 * 1000));
  const todayJobs = [];
  const tomorrowJobs = [];
  jobs.forEach(job => {
    const due = job.dueDate ? startOfDay(new Date(job.dueDate)) : null;
    if (!due) return;
    if (differenceInCalendarDays(due, today) <= 0) {
      todayJobs.push(job);
    } else if (differenceInCalendarDays(due, tomorrow) === 0) {
      tomorrowJobs.push(job);
    }
  });
  return { todayJobs, tomorrowJobs, today };
};

/**
 * Rows saved in lockedPrintLists only contained fields from cleanJob(), so `printLocations`
 * was missing and DTG/DTF showed 0/0/0. Merge each locked row with live `orderLines` data by id.
 */
const mergeLockedJobsWithPool = (lockedJobs, poolJobs) => {
  const poolById = new Map((poolJobs || []).map((j) => [j.id, j]));
  return (lockedJobs || []).map((j) => {
    const full = poolById.get(j.id);
    if (!full) return j;
    return {
      ...full,
      ...j,
      printLocations:
        Array.isArray(j.printLocations) && j.printLocations.length > 0
          ? j.printLocations
          : Array.isArray(full.printLocations)
            ? full.printLocations
            : []
    };
  });
};

/** 'screen' = ink counts; 'dtg_dtf' / 'embroidery' = front/back/other locations (aligned with LineItemCompact). */
const getLocationOrColorDisplay = (line, mode = 'screen') => {
  if (mode === 'screen') {
    return `${line.frontInkColors?.length || 0} / ${line.backInkColors?.length || 0} / ${line.otherInkColors?.length || 0}`;
  }
  if (mode === 'dtg_dtf') {
    const raw = line.printLocations;
    const locs = Array.isArray(raw) ? raw : raw && typeof raw === 'object' ? Object.values(raw) : [];
    let front;
    let back;
    let other;
    if (locs.length > 0) {
      front = locs.some((l) => String(l).toLowerCase() === 'front') ? 1 : 0;
      back = locs.some((l) => String(l).toLowerCase() === 'back') ? 1 : 0;
      other = locs.some((l) => String(l).toLowerCase() === 'other') ? 1 : 0;
    } else {
      front = Array.isArray(line.frontInkColors) && line.frontInkColors.length > 0 ? 1 : 0;
      back = Array.isArray(line.backInkColors) && line.backInkColors.length > 0 ? 1 : 0;
      other = Array.isArray(line.otherInkColors) && line.otherInkColors.length > 0 ? 1 : 0;
    }
    return `${front} / ${back} / ${other}`;
  }
  // embroidery — locations from printLocations or presence of thread colors per side
  const rawEmb = line.printLocations;
  const locs = Array.isArray(rawEmb) ? rawEmb : rawEmb && typeof rawEmb === 'object' ? Object.values(rawEmb) : [];
  if (locs.length > 0) {
    const front = locs.some((l) => String(l).toLowerCase() === 'front') ? 1 : 0;
    const back = locs.some((l) => String(l).toLowerCase() === 'back') ? 1 : 0;
    const other = locs.some((l) => String(l).toLowerCase() === 'other') ? 1 : 0;
    return `${front} / ${back} / ${other}`;
  }
  const front = Array.isArray(line.frontThreadColors) && line.frontThreadColors.length > 0 ? 1 : 0;
  const back = Array.isArray(line.backThreadColors) && line.backThreadColors.length > 0 ? 1 : 0;
  const other = Array.isArray(line.otherThreadColors) && line.otherThreadColors.length > 0 ? 1 : 0;
  return `${front} / ${back} / ${other}`;
};

/** Two-line header so "Colors/Locations" and "(F/B/O)" don't collide with Est. in narrow columns */
const getLocationColumnHeaderLines = (mode = 'screen') =>
  mode === 'screen'
    ? { line1: 'Colors', line2: '(F/B/O)' }
    : { line1: 'Locations', line2: '(F/B/O)' };

/**
 * Today | Suggested Tomorrow — two equal columns, no bleed into each other.
 * minmax(0,1fr) lets tracks shrink; overflow on direct children clips table paint to the cell.
 */
const SCHEDULE_PAIR_GRID_SX = {
  display: 'grid',
  boxSizing: 'border-box',
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  gap: 3,
  alignItems: 'start',
  gridTemplateColumns: {
    xs: 'minmax(0, 1fr)',
    md: 'repeat(2, minmax(0, 1fr))'
  },
  '& > *': {
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    boxSizing: 'border-box'
  }
};

const SCHEDULE_PAIR_ROW_SX = SCHEDULE_PAIR_GRID_SX;

/** Tab title + circular count of lines on today’s schedule for that discipline (hidden when 0) */
const PrintListTabLabel = ({ text, count }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: count > 0 ? 1 : 0 }}>
    <Box component="span">{text}</Box>
    {count > 0 ? (
      <Box
        component="span"
        aria-label={`${count} line${count === 1 ? '' : 's'} remaining`}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 18,
          height: 18,
          px: count > 99 ? 0.35 : 0.5,
          borderRadius: '999px',
          bgcolor: 'var(--al-primary)',
          color: 'var(--al-bg)',
          fontSize: '0.65rem',
          fontWeight: 700,
          lineHeight: 1,
          flexShrink: 0
        }}
      >
        {count}
      </Box>
    ) : null}
  </Box>
);

const SummaryBox = styled(Box)`
  background: var(--al-surface);
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 24px;
  border: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 768px) {
    padding: 14px 12px;
    margin-bottom: 16px;
    border-radius: 10px;
  }
`;

const SummaryGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    overflow-x: auto;
    gap: 12px;
    padding-bottom: 6px;
    margin: 0 -4px;
    padding-left: 4px;
    padding-right: 4px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;

    & > * {
      flex: 0 0 auto;
      min-width: 118px;
      scroll-snap-align: start;
    }
  }
`;

const SummaryItem = styled(Box)`
  text-align: center;
`;

const SummaryLabel = styled(Typography)`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9em;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
`;

const SummaryValue = styled(Typography)`
  color: var(--al-primary);
  font-size: 1.8em;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.04em;

  @media (max-width: 768px) {
    font-size: 1.4em;
  }
`;

const MobileJobCard = styled.div`
  background-color: var(--al-surface);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 14px 14px 12px;
  margin-bottom: 10px;
  touch-action: manipulation;

  ${props => props.isOverdue && `
    border-left: 4px solid #FF4C4C;
  `}

  ${props => props.printed && `
    opacity: 0.72;
  `}
`;

const MobileJobActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

/** Suggested Tomorrow — layout comes from SCHEDULE_PAIR_GRID_SX parent; this is mobile-only separation */
const TomorrowBlock = styled(Box)`
  width: 100%;

  @media (max-width: 768px) {
    padding-top: 20px;
    margin-top: 4px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
};

const MobileTable = React.memo(({ jobs, today, title, fallbackMessage, handlePrintedChange, handleOpenLineDetail, onExcludeFromToday, locationColumnMode = 'screen', customerNameByLineId }) => {
  return (
    <Box sx={{ flex: 1, width: '100%', minWidth: 0 }}>
      <Typography
        variant="h6"
        sx={{
          color: 'var(--al-primary)',
          fontWeight: 600,
          mb: 1.5,
          fontSize: { xs: '0.95rem', sm: '1.15rem' },
          letterSpacing: '0.04em',
          textTransform: 'uppercase'
        }}
      >
        {title}
      </Typography>
      {jobs.length === 0 && title.includes('Today') ? (
        <PrintlistSuccessAnimation />
      ) : jobs.length > 0 ? (
        <div>
          {jobs.map((job) => (
            <MobileJobCard
              key={job.id}
              isOverdue={isOverdue(job, today)}
              printed={job.printed}
              onClick={() => handleOpenLineDetail(job)}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  color: job.printed ? 'rgba(255,255,255,0.45)' : '#fff',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  lineHeight: 1.35,
                  pr: 1,
                  textDecoration: job.printed ? 'line-through' : 'none',
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em'
                }}
              >
                {job.designName || 'Unnamed Design'}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 0.75,
                  color: 'rgba(255,255,255,0.55)',
                  fontSize: '0.72rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.02em'
                }}
              >
                {job.customerName || customerNameByLineId?.get(job.id) || '—'}
                {' · '}
                Order {job.orderNumber || '—'}
                {job.dueDate ? (
                  <>
                    {' · '}
                    Due {formatDate(job.dueDate)}
                  </>
                ) : null}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1.25,
                  mt: 1.25,
                  typography: 'caption',
                  fontSize: '0.75rem',
                  color: 'rgba(255,255,255,0.75)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.02em'
                }}
              >
                <span>Qty {job.totalQuantity ?? 0}</span>
                <span>
                  {locationColumnMode === 'screen' ? 'Colors' : 'Locations'} (F/B/O):{' '}
                  {getLocationOrColorDisplay(job, locationColumnMode)}
                </span>
                <span>{isNaN(job.estimatedTime) ? '—' : `${Math.round(job.estimatedTime)} min`}</span>
              </Box>
              <MobileJobActions onClick={(e) => e.stopPropagation()}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <input
                    type="checkbox"
                    checked={job.printed || false}
                    onChange={() => handlePrintedChange(job)}
                    aria-label="Printed"
                    style={{ cursor: 'pointer', width: 18, height: 18 }}
                  />
                </Box>
                {onExcludeFromToday ? (
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    sx={{ flexShrink: 0, py: 0.5, px: 1.25, fontSize: '0.65rem' }}
                    onClick={() => onExcludeFromToday(job)}
                  >
                    Exclude
                  </Button>
                ) : (
                  <Box sx={{ width: 8 }} />
                )}
              </MobileJobActions>
            </MobileJobCard>
          ))}
        </div>
      ) : (
        <Typography
          variant="body2"
          sx={{ color: 'rgba(255, 255, 255, 0.65)', mt: 1.5, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}
        >
          {fallbackMessage}
        </Typography>
      )}
    </Box>
  );
});

const DesktopTable = React.memo(
  ({
    jobs,
    today,
    title,
    fallbackMessage,
    handlePrintedChange,
    handleOpenLineDetail,
    onExcludeFromToday,
    locationColumnMode = 'screen',
    customerNameByLineId,
    scheduleColumnSpacer = false
  }) => {
  const locHeader = getLocationColumnHeaderLines(locationColumnMode);
  const hasScheduleColumn = Boolean(onExcludeFromToday) || scheduleColumnSpacer;
  return (
    <Box sx={{ minWidth: 0, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
      <Typography
        variant="h6"
        sx={{ color: 'var(--al-primary)', fontWeight: 600, mb: 2, textTransform: 'uppercase', letterSpacing: '0.05em' }}
      >
        {title}
      </Typography>
      {jobs.length === 0 && title.includes('Today') ? (
        <PrintlistSuccessAnimation />
      ) : jobs.length > 0 ? (
        <TableContainer
          component={Paper}
          sx={{
            mt: 2,
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            display: 'block',
            backgroundColor: 'var(--al-surface)',
            boxShadow: 'none',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          <Table
            size="small"
            sx={{
              width: '100%',
              maxWidth: '100%',
              tableLayout: 'fixed',
              borderCollapse: 'collapse'
            }}
          >
            <colgroup>
              {hasScheduleColumn ? (
                <>
                  <col style={{ width: '3%' }} />
                  <col style={{ width: '18%' }} />
                  <col style={{ width: '41%' }} />
                  <col style={{ width: '4%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '7%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '10%' }} />
                </>
              ) : (
                <>
                  <col style={{ width: '4%' }} />
                  <col style={{ width: '19%' }} />
                  <col style={{ width: '38%' }} />
                  <col style={{ width: '5%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '7%' }} />
                </>
              )}
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell
                  scope="col"
                  aria-label="Printed"
                  sx={{ width: 44, maxWidth: 44, px: '4px', py: 1, textAlign: 'center', verticalAlign: 'bottom' }}
                />
                <TableCell sx={{ px: 1, py: 1, verticalAlign: 'bottom', whiteSpace: 'nowrap' }}>
                  Customer
                </TableCell>
                <TableCell sx={{ px: 1, py: 1, verticalAlign: 'bottom', whiteSpace: 'nowrap' }}>
                  Design Name
                </TableCell>
                <TableCell sx={{ width: 56, px: 0.75, py: 1, textAlign: 'center', verticalAlign: 'bottom' }}>Qty</TableCell>
                <TableCell
                  sx={{
                    px: 1,
                    py: 1,
                    textAlign: 'center',
                    verticalAlign: 'bottom',
                    lineHeight: 1.25
                  }}
                >
                  <Box component="span" sx={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.04em' }}>
                    {locHeader.line1}
                  </Box>
                  <Box component="span" sx={{ display: 'block', fontSize: '0.68rem', opacity: 0.88, mt: 0.35, letterSpacing: '0.03em' }}>
                    {locHeader.line2}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    px: 0.75,
                    py: 1,
                    textAlign: 'center',
                    verticalAlign: 'bottom',
                    whiteSpace: 'nowrap',
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    letterSpacing: '0.05em'
                  }}
                >
                  Due
                </TableCell>
                <TableCell
                  sx={{
                    px: 1,
                    py: 1,
                    textAlign: 'center',
                    verticalAlign: 'bottom',
                    whiteSpace: 'nowrap',
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    letterSpacing: '0.05em'
                  }}
                >
                  Est.
                </TableCell>
                {hasScheduleColumn ? (
                  <TableCell
                    align="right"
                    sx={{ px: 0.25, py: 1, verticalAlign: 'bottom', whiteSpace: 'nowrap' }}
                  >
                    {onExcludeFromToday ? 'Schedule' : '\u00a0'}
                  </TableCell>
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((job) => (
                <TableRow
                  key={job.id}
                  sx={{
                    '&:hover': { backgroundColor: 'rgba(var(--al-primary-rgb), 0.05)', cursor: 'pointer' },
                    ...(job.printed && {
                      '& td': {
                        textDecoration: 'line-through',
                        color: 'rgba(255, 255, 255, 0.5)'
                      }
                    }),
                    ...(isOverdue(job, today) && {
                      '& td': {
                        color: '#FF4C4C',
                        fontWeight: 700
                      }
                    })
                  }}
                  onClick={() => handleOpenLineDetail(job)}
                >
                  <TableCell
                    onClick={e => e.stopPropagation()}
                    sx={{ width: 40, maxWidth: 44, px: '4px', py: 0.5, textAlign: 'center', verticalAlign: 'middle' }}
                  >
                    <input
                      type="checkbox"
                      checked={job.printed || false}
                      onChange={() => handlePrintedChange(job)}
                      aria-label="Printed"
                      style={{ cursor: 'pointer', margin: 0 }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      px: 1,
                      py: 0.75,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      textTransform: 'uppercase',
                      fontSize: '0.8rem',
                      verticalAlign: 'middle',
                      letterSpacing: '0.02em'
                    }}
                    title={String(job.customerName || customerNameByLineId?.get(job.id) || '')}
                  >
                    {job.customerName || customerNameByLineId?.get(job.id) || '—'}
                  </TableCell>
                  <TableCell
                    sx={{
                      px: 1,
                      py: 0.75,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      textTransform: 'uppercase',
                      fontSize: '0.8rem',
                      verticalAlign: 'middle',
                      letterSpacing: '0.02em'
                    }}
                    title={String(job.designName || '')}
                  >
                    {job.designName || 'Unnamed Design'}
                  </TableCell>
                  <TableCell sx={{ px: 0.75, py: 0.5, textAlign: 'center', textTransform: 'uppercase' }}>{job.totalQuantity}</TableCell>
                  <TableCell
                    sx={{
                      px: 1,
                      py: 0.5,
                      textAlign: 'center',
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {getLocationOrColorDisplay(job, locationColumnMode)}
                  </TableCell>
                  <TableCell
                    sx={{
                      px: 0.75,
                      py: 0.5,
                      textAlign: 'center',
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                      whiteSpace: 'nowrap'
                    }}
                    title={job.dueDate ? String(formatDate(job.dueDate)) : undefined}
                  >
                    {job.dueDate ? formatDate(job.dueDate) : '—'}
                  </TableCell>
                  <TableCell
                    sx={{
                      px: 0.75,
                      py: 0.5,
                      textAlign: 'center',
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {isNaN(job.estimatedTime) ? 'N/A' : `${Math.round(job.estimatedTime)} min`}
                  </TableCell>
                  {hasScheduleColumn ? (
                    <TableCell
                      align="right"
                      onClick={onExcludeFromToday ? (e) => e.stopPropagation() : undefined}
                      sx={{ px: 0.25, py: 0.5, whiteSpace: 'nowrap' }}
                    >
                      {onExcludeFromToday ? (
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          sx={{ fontSize: '0.62rem', py: 0.25, px: 0.75, minWidth: 0, lineHeight: 1.2 }}
                          onClick={() => onExcludeFromToday(job)}
                        >
                          Exclude
                        </Button>
                      ) : (
                        '\u00a0'
                      )}
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography
          variant="body1"
          sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 2, textTransform: 'uppercase', letterSpacing: '0.03em' }}
        >
          {fallbackMessage}
        </Typography>
      )}
    </Box>
  );
});

const OrderTable = React.memo(
  ({
    jobs,
    today,
    title,
    fallbackMessage,
    handlePrintedChange,
    handleOpenLineDetail,
    onExcludeFromToday,
    locationColumnMode = 'screen',
    customerNameByLineId,
    scheduleColumnSpacer = false
  }) => {
  const isMobile = useIsMobile();
  return isMobile ? (
    <MobileTable
      jobs={jobs}
      today={today}
      title={title}
      fallbackMessage={fallbackMessage}
      handlePrintedChange={handlePrintedChange}
      handleOpenLineDetail={handleOpenLineDetail}
      onExcludeFromToday={onExcludeFromToday}
      locationColumnMode={locationColumnMode}
      customerNameByLineId={customerNameByLineId}
    />
  ) : (
    <DesktopTable
      jobs={jobs}
      today={today}
      title={title}
      fallbackMessage={fallbackMessage}
      handlePrintedChange={handlePrintedChange}
      handleOpenLineDetail={handleOpenLineDetail}
      onExcludeFromToday={onExcludeFromToday}
      locationColumnMode={locationColumnMode}
      customerNameByLineId={customerNameByLineId}
      scheduleColumnSpacer={scheduleColumnSpacer}
    />
  );
});

const PrintList = () => {
  const { facilityId } = useFacility();
  // State for job lists
  const [jobsWithEstimatedTimes, setJobsWithEstimatedTimes] = useState([]);
  const [dtgJobsAll, setDtgJobsAll] = useState([]);
  const [dtfJobsAll, setDtfJobsAll] = useState([]);
  const [embroideryJobsAll, setEmbroideryJobsAll] = useState([]);
  const [screenPrintJobsAll, setScreenPrintJobsAll] = useState([]);
  const [dtgToday, setDtgToday] = useState(null);
  const [dtfToday, setDtfToday] = useState(null);
  const [screenToday, setScreenToday] = useState(null);
  const [activeTab, setActiveTab] = useState('Screen Print');
  const [printList, setPrintList] = useState([]);
  const customerNameByLineId = useMemo(() => {
    const m = new Map();
    printList.forEach((l) => {
      if (l?.id && l.customerName) m.set(l.id, l.customerName);
    });
    return m;
  }, [printList]);

  const [lockedPrintList, setLockedPrintList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showQCModal, setShowQCModal] = useState(false);
  const [selectedLine, setSelectedLine] = useState(null);
  const [lineDetailOpen, setLineDetailOpen] = useState(false);
  const [lineDetailData, setLineDetailData] = useState(null);
  const [lockedScreenPrintToday, setLockedScreenPrintToday] = useState([]);
  const [lockedDTGToday, setLockedDTGToday] = useState([]);
  const [lockedDTFToday, setLockedDTFToday] = useState([]);
  const [lockedEmbroideryToday, setLockedEmbroideryToday] = useState([]);
  const [screenPrintLockedAt, setScreenPrintLockedAt] = useState(null);
  const [dtgLockedAt, setDTGLockedAt] = useState(null);
  const [dtfLockedAt, setDTFLockedAt] = useState(null);
  const [embroideryLockedAt, setEmbroideryLockedAt] = useState(null);
  /** Line IDs excluded from today's schedule for this method (persisted); schedule is refilled from the remaining pool. */
  const [excludedScreenPrint, setExcludedScreenPrint] = useState([]);
  const [excludedDTG, setExcludedDTG] = useState([]);
  const [excludedDTF, setExcludedDTF] = useState([]);
  const [excludedEmbroidery, setExcludedEmbroidery] = useState([]);
  const [recalcScreenDialogOpen, setRecalcScreenDialogOpen] = useState(false);
  const [recalcDTGDialogOpen, setRecalcDTGDialogOpen] = useState(false);
  const [recalcDTFDialogOpen, setRecalcDTFDialogOpen] = useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);

  /** After last line on an order is QC-printed, offer box labels (same flow as OrderView). */
  const [boxDialogOpen, setBoxDialogOpen] = useState(false);
  const [boxPrintOrder, setBoxPrintOrder] = useState(null);
  const boxPrintOrderRef = useRef(null);
  const [boxToPrint, setBoxToPrint] = useState(null);
  const [currentBoxPrinting, setCurrentBoxPrinting] = useState(null);
  const boxPrintingRef = useRef(false);

  const lockedDTGTodayForDisplay = useMemo(
    () => mergeLockedJobsWithPool(lockedDTGToday, dtgJobsAll),
    [lockedDTGToday, dtgJobsAll]
  );
  const lockedDTFTodayForDisplay = useMemo(
    () => mergeLockedJobsWithPool(lockedDTFToday, dtfJobsAll),
    [lockedDTFToday, dtfJobsAll]
  );

  const fetchPrintList = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const q = query(
        collection(db, 'orderLines'),
        where('status', '==', LINE_STATUSES.READY_TO_PRINT)
      );
      const querySnapshot = await getDocs(q);
      const lines = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Enrich lines with order data (poNumber -> orderNumber, dueDate, customer, PM)
      const enrichedLines = await Promise.all(lines.map(async (line) => {
        if (line.orderId) {
          try {
            const orderDoc = await getDoc(doc(db, 'orders', line.orderId));
            if (orderDoc.exists()) {
              const orderData = orderDoc.data();
              return {
                ...line,
                facilityId: line.facilityId || orderData.facilityId,
                orderNumber: orderData.poNumber || 'N/A',
                dueDate: orderData.dueDate || null,
                customerName: orderData.customerName || line.customerName || '',
                projectManager: orderData.projectManager || 'N/A'
              };
            }
          } catch (err) {
            console.warn('Could not fetch order for line:', line.id, err);
          }
        }
        return {
          ...line,
          orderNumber: line.orderNumber || 'N/A',
          dueDate: line.dueDate || null,
          customerName: line.customerName || '',
          projectManager: line.projectManager || 'N/A'
        };
      }));

      setPrintList(filterByFacility(enrichedLines, facilityId));
    } catch (error) {
      console.error('Error fetching print list:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Load locked print lists from Firestore on mount / facility change
  useEffect(() => {
    const loadLockedPrintLists = async () => {
      try {
        const today = startOfDay(new Date());
        const lockedListRef = doc(
          db,
          'lockedPrintLists',
          facilityProgressDocId(facilityId, today.toISOString().split('T')[0])
        );
        const lockedListDoc = await getDoc(lockedListRef);
        
        if (lockedListDoc.exists()) {
          const lockedData = lockedListDoc.data();
          console.log('Loaded locked print lists from Firestore:', lockedData);
          
          // Set locked lists from Firestore
          if (lockedData.screenPrint) {
            setLockedScreenPrintToday(lockedData.screenPrint);
          }
          if (lockedData.dtg) {
            setLockedDTGToday(lockedData.dtg);
          }
          if (lockedData.dtf) {
            setLockedDTFToday(lockedData.dtf);
          }
          if (lockedData.embroidery) {
            setLockedEmbroideryToday(lockedData.embroidery);
          }
          if (Array.isArray(lockedData.excludedScreenPrint)) {
            setExcludedScreenPrint(lockedData.excludedScreenPrint);
          }
          if (Array.isArray(lockedData.excludedDTG)) {
            setExcludedDTG(lockedData.excludedDTG);
          }
          if (Array.isArray(lockedData.excludedDTF)) {
            setExcludedDTF(lockedData.excludedDTF);
          }
          if (Array.isArray(lockedData.excludedEmbroidery)) {
            setExcludedEmbroidery(lockedData.excludedEmbroidery);
          }
          
          // Set locked timestamps if they exist
          if (lockedData.screenPrintLockedAt) {
            setScreenPrintLockedAt(lockedData.screenPrintLockedAt.toDate());
          }
          if (lockedData.dtgLockedAt) {
            setDTGLockedAt(lockedData.dtgLockedAt.toDate());
          }
          if (lockedData.dtfLockedAt) {
            setDTFLockedAt(lockedData.dtfLockedAt.toDate());
          }
          if (lockedData.embroideryLockedAt) {
            setEmbroideryLockedAt(lockedData.embroideryLockedAt.toDate());
          }
        } else {
          setLockedScreenPrintToday([]);
          setLockedDTGToday([]);
          setLockedDTFToday([]);
          setLockedEmbroideryToday([]);
          setExcludedScreenPrint([]);
          setExcludedDTG([]);
          setExcludedDTF([]);
          setExcludedEmbroidery([]);
          setScreenPrintLockedAt(null);
          setDTGLockedAt(null);
          setDTFLockedAt(null);
          setEmbroideryLockedAt(null);
        }
      } catch (error) {
        console.error('Error loading locked print lists:', error);
      }
    };
    
    loadLockedPrintLists();
    fetchPrintList();
  }, [facilityId]);

  // Calculate job lists whenever printList changes
  useEffect(() => {
    const result = calculateOptimalPrintlist(printList);
    setJobsWithEstimatedTimes([...result.dtgJobs, ...result.dtfJobs, ...result.embroideryJobs, ...result.screenPrintJobs]);
    
    const dtgJobs = result.dtgJobs;
    const dtfJobs = result.dtfJobs;
    const embroideryJobs = result.embroideryJobs;
    const screenJobs = result.screenPrintJobs;
    
    setDtgJobsAll(dtgJobs);
    setDtfJobsAll(dtfJobs);
    setEmbroideryJobsAll(embroideryJobs);
    setScreenPrintJobsAll(screenJobs);
    
    const { today: dtgTodayDate } = splitJobsByDay(dtgJobs);
    const { today: dtfTodayDate } = splitJobsByDay(dtfJobs);
    const { today: screenTodayDate } = splitJobsByDay(screenJobs);
    
    setDtgToday(dtgTodayDate);
    setDtfToday(dtfTodayDate);
    setScreenToday(screenTodayDate);
  }, [printList]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  useEffect(() => {
    if (activeTab === 'Embroidery') {
      setActiveTab('Screen Print');
    }
  }, [activeTab]);

  const handlePrintedChange = async (line) => {
    setSelectedLine(line);
    setShowQCModal(true);
  };

  // Helper function to check if all lines in an order are completed and update order status
  const checkAndUpdateOrderStatus = async (orderId) => {
    if (!orderId) return { allCompleted: false };
    try {
      const orderRef = doc(db, 'orders', orderId);
      const orderSnap = await getDoc(orderRef);
      if (!orderSnap.exists()) return { allCompleted: false };

      const currentOrderStatus = orderSnap.data()?.status;
      const terminalStatuses = [
        ORDER_STATUSES.READY_TO_INVOICE,
        ORDER_STATUSES.COMPLETED,
        ORDER_STATUSES.CANCELLED,
        ORDER_STATUSES.WAITING_ON_REPRINT
      ];
      if (terminalStatuses.includes(currentOrderStatus)) {
        return { allCompleted: false, skipped: true, reason: 'terminal_status', currentOrderStatus };
      }

      const linesQuery = query(
        collection(db, 'orderLines'),
        where('orderId', '==', orderId)
      );
      const linesSnapshot = await getDocs(linesQuery);
      const lines = linesSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

      const allCompleted =
        lines.length > 0 && lines.every((line) => line.status === LINE_STATUSES.COMPLETED);

      if (allCompleted) {
        await updateDoc(orderRef, {
          status: ORDER_STATUSES.READY_TO_INVOICE,
          updatedAt: serverTimestamp()
        });
        await syncOrderPaymentIfInvoiceable(db, orderId);
        console.log(`✅ Order ${orderId} updated to Ready To Invoice - all lines completed`);
      }
      return { allCompleted };
    } catch (error) {
      console.error('Error checking/updating order status:', error);
      return { allCompleted: false };
    }
  };

  const handlePrintBoxesFromPrintList = async (totalBoxes) => {
    const order = boxPrintOrderRef.current;
    if (!order || boxPrintingRef.current) return;
    const count = parseInt(totalBoxes, 10);
    if (!Number.isFinite(count) || count < 1) return;

    const lines = order.lines || [];
    const { totalPieces, total } = computeBoxStickerTotals(order, lines);
    const totalStr = total.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    const invoiceNumber = order.orderNumber || order.poNumber;

    try {
      boxPrintingRef.current = true;
      setBoxDialogOpen(false);

      for (let boxNumber = 1; boxNumber <= count; boxNumber++) {
        setCurrentBoxPrinting({ currentBox: boxNumber, totalBoxes: count });
        const printedSuccessfully = await new Promise((resolve) => {
          setBoxToPrint({
            currentBox: boxNumber,
            totalBoxes: count,
            invoiceNumber,
            client: order.customerName,
            totalStr,
            manager: order.projectManager,
            pieces: totalPieces,
            onComplete: (success) => resolve(success)
          });
        });
        setBoxToPrint(null);
        if (!printedSuccessfully) break;
      }
    } catch (err) {
      console.error('Box print sequence error:', err);
    } finally {
      setCurrentBoxPrinting(null);
      boxPrintingRef.current = false;
      setBoxToPrint(null);
      boxPrintOrderRef.current = null;
      setBoxPrintOrder(null);
    }
  };

  const handleQCComplete = async (lineId, status, data) => {
    let orderAllCompletedForBoxFlow = false;
    let orderId;
    try {
      const lineRef = doc(db, 'orderLines', lineId);
      const lineDoc = await getDoc(lineRef);
      orderId = lineDoc.data()?.orderId;
      const prevStatus = lineDoc.data()?.status;

      if (status === 'PRINTED' || status === 'PARTIALLY_PRINTED' || status === 'REDUCED') {
        const blockReason = await reasonSpLineCannotPrint(db, lineDoc.data());
        if (blockReason) {
          alert(blockReason);
          return;
        }
      }
      
      if (status === 'PRINTED') {
        // Update line to completed status
        await updateDoc(lineRef, {
          status: LINE_STATUSES.COMPLETED,
          printed: true,
          updatedAt: serverTimestamp(),
          ...activityLogAppend('Marked as Printed', { from: prevStatus, to: LINE_STATUSES.COMPLETED })
        });
        
        if (orderId) {
          const { allCompleted } = await checkAndUpdateOrderStatus(orderId);
          orderAllCompletedForBoxFlow = allCompleted;
        }
        
        // Remove from locked list for the active tab and update Firestore
        let updatedScreenPrint = lockedScreenPrintToday;
        let updatedDTG = lockedDTGToday;
        let updatedDTF = lockedDTFToday;
        let updatedEmbroidery = lockedEmbroideryToday;
        
        if (activeTab === 'DTG') {
          updatedDTG = lockedDTGToday.filter(line => line.id !== lineId);
          setLockedDTGToday(updatedDTG);
        } else if (activeTab === 'DTF') {
          updatedDTF = lockedDTFToday.filter(line => line.id !== lineId);
          setLockedDTFToday(updatedDTF);
        } else if (activeTab === 'Embroidery') {
          updatedEmbroidery = lockedEmbroideryToday.filter(line => line.id !== lineId);
          setLockedEmbroideryToday(updatedEmbroidery);
        } else {
          updatedScreenPrint = lockedScreenPrintToday.filter(line => line.id !== lineId);
          setLockedScreenPrintToday(updatedScreenPrint);
        }
        
        // Update the locked print list in Firestore
        await lockPrintList(updatedScreenPrint, updatedDTG, updatedDTF, updatedEmbroidery, {
          excludedScreenPrint,
          excludedDTG,
          excludedDTF,
          excludedEmbroidery
        });
      } else if (status === 'PARTIALLY_PRINTED' && data?.reorderLine) {
        // Handle reorder creation
        await updateDoc(lineRef, {
          status: LINE_STATUSES.COMPLETED,
          printed: true,
          updatedAt: serverTimestamp(),
          hasReorder: true,
          ...activityLogAppend('Marked as Printed', { from: prevStatus, to: LINE_STATUSES.COMPLETED, details: 'Reorder created' })
        });

        // Create new reorder line
        await addDoc(collection(db, 'orderLines'), {
          ...data.reorderLine,
          status: LINE_STATUSES.READY_TO_ORDER,
          isReorder: true,
          originalLineId: lineId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        
        if (orderId) {
          const { allCompleted } = await checkAndUpdateOrderStatus(orderId);
          orderAllCompletedForBoxFlow = allCompleted;
        }
        
        // Remove from locked list and update Firestore
        let updatedScreenPrint = lockedScreenPrintToday;
        let updatedDTG = lockedDTGToday;
        let updatedDTF = lockedDTFToday;
        let updatedEmbroidery = lockedEmbroideryToday;
        
        if (activeTab === 'DTG') {
          updatedDTG = lockedDTGToday.filter(line => line.id !== lineId);
          setLockedDTGToday(updatedDTG);
        } else if (activeTab === 'DTF') {
          updatedDTF = lockedDTFToday.filter(line => line.id !== lineId);
          setLockedDTFToday(updatedDTF);
        } else if (activeTab === 'Embroidery') {
          updatedEmbroidery = lockedEmbroideryToday.filter(line => line.id !== lineId);
          setLockedEmbroideryToday(updatedEmbroidery);
        } else {
          updatedScreenPrint = lockedScreenPrintToday.filter(line => line.id !== lineId);
          setLockedScreenPrintToday(updatedScreenPrint);
        }
        
        await lockPrintList(updatedScreenPrint, updatedDTG, updatedDTF, updatedEmbroidery, {
          excludedScreenPrint,
          excludedDTG,
          excludedDTF,
          excludedEmbroidery
        });
      } else if (status === 'REDUCED') {
        // Update line with reduced quantities
        await updateDoc(lineRef, {
          status: LINE_STATUSES.COMPLETED,
          printed: true,
          sizes: data.originalLine.sizes,
          totalQuantity: data.originalLine.totalQuantity,
          updatedAt: serverTimestamp(),
          ...activityLogAppend('Marked as Printed', { from: prevStatus, to: LINE_STATUSES.COMPLETED, details: 'Quantities reduced' })
        });
        
        if (orderId) {
          const { allCompleted } = await checkAndUpdateOrderStatus(orderId);
          orderAllCompletedForBoxFlow = allCompleted;
        }
        
        // Remove from locked list and update Firestore
        let updatedScreenPrint = lockedScreenPrintToday;
        let updatedDTG = lockedDTGToday;
        let updatedDTF = lockedDTFToday;
        let updatedEmbroidery = lockedEmbroideryToday;
        
        if (activeTab === 'DTG') {
          updatedDTG = lockedDTGToday.filter(line => line.id !== lineId);
          setLockedDTGToday(updatedDTG);
        } else if (activeTab === 'DTF') {
          updatedDTF = lockedDTFToday.filter(line => line.id !== lineId);
          setLockedDTFToday(updatedDTF);
        } else if (activeTab === 'Embroidery') {
          updatedEmbroidery = lockedEmbroideryToday.filter(line => line.id !== lineId);
          setLockedEmbroideryToday(updatedEmbroidery);
        } else {
          updatedScreenPrint = lockedScreenPrintToday.filter(line => line.id !== lineId);
          setLockedScreenPrintToday(updatedScreenPrint);
        }
        
        await lockPrintList(updatedScreenPrint, updatedDTG, updatedDTF, updatedEmbroidery, {
          excludedScreenPrint,
          excludedDTG,
          excludedDTF,
          excludedEmbroidery
        });
      }
      
      setShowQCModal(false);
      setSelectedLine(null);
      await fetchPrintList();

      if (orderId && orderAllCompletedForBoxFlow) {
        try {
          const orderSnap = await getDoc(doc(db, 'orders', orderId));
          if (orderSnap.exists()) {
            const linesQ = query(collection(db, 'orderLines'), where('orderId', '==', orderId));
            const linesSnap = await getDocs(linesQ);
            const lines = linesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
            const bundle = { id: orderId, ...orderSnap.data(), lines };
            boxPrintOrderRef.current = bundle;
            setBoxPrintOrder(bundle);
            setBoxDialogOpen(true);
          }
        } catch (loadErr) {
          console.warn('Could not load order for box labels:', loadErr);
        }
      }
    } catch (error) {
      console.error('Error updating line:', error);
      alert('Error processing line update: ' + error.message);
    }
  };

  // Helper to open line detail modal (simplified view for print list)
  const handleOpenLineDetail = (job) => {
    setLineDetailData(job);
    setLineDetailOpen(true);
  };

  // Helper to fill a day's jobs by minutes (as in fillDay)
  const fillMinutes = (jobs) => {
    let minutesFilled = 0;
    const todaysJobs = [];
    for (const line of jobs) {
      const est = line.estimatedTime || 0;
      if (minutesFilled === 0 || minutesFilled + est <= DAILY_MINUTES) {
        todaysJobs.push(line);
        minutesFilled += est;
      } else {
        break;
      }
    }
    return todaysJobs;
  };

  /** Tomorrow preview: not on today’s locked list and not PM-excluded for the rest of today. */
  const suggestedTomorrowFromPool = (lockedJobs, allJobs, excludedIds = []) => {
    const todayIds = new Set((lockedJobs || []).map((j) => j.id));
    const excluded = new Set(excludedIds || []);
    return fillMinutes(
      (allJobs || []).filter((j) => !todayIds.has(j.id) && !excluded.has(j.id))
    );
  };

  const handleExcludeFromToday = async (job) => {
    if (!job?.id) return;
    try {
      let exS = [...excludedScreenPrint];
      let exDtg = [...excludedDTG];
      let exDtf = [...excludedDTF];
      let exEmb = [...excludedEmbroidery];
      let newScreen = lockedScreenPrintToday;
      let newDtg = lockedDTGToday;
      let newDtf = lockedDTFToday;
      let newEmb = lockedEmbroideryToday;

      if (activeTab === 'DTG') {
        if (!exDtg.includes(job.id)) exDtg.push(job.id);
        newDtg = fillMinutes(dtgJobsAll.filter((j) => !exDtg.includes(j.id)));
        setExcludedDTG(exDtg);
        setLockedDTGToday(newDtg);
      } else if (activeTab === 'DTF') {
        if (!exDtf.includes(job.id)) exDtf.push(job.id);
        newDtf = fillMinutes(dtfJobsAll.filter((j) => !exDtf.includes(j.id)));
        setExcludedDTF(exDtf);
        setLockedDTFToday(newDtf);
      } else if (activeTab === 'Embroidery') {
        if (!exEmb.includes(job.id)) exEmb.push(job.id);
        newEmb = fillMinutes(embroideryJobsAll.filter((j) => !exEmb.includes(j.id)));
        setExcludedEmbroidery(exEmb);
        setLockedEmbroideryToday(newEmb);
      } else {
        if (!exS.includes(job.id)) exS.push(job.id);
        newScreen = fillMinutes(screenPrintJobsAll.filter((j) => !exS.includes(j.id)));
        setExcludedScreenPrint(exS);
        setLockedScreenPrintToday(newScreen);
      }

      await lockPrintList(newScreen, newDtg, newDtf, newEmb, {
        excludedScreenPrint: exS,
        excludedDTG: exDtg,
        excludedDTF: exDtf,
        excludedEmbroidery: exEmb
      });
    } catch (err) {
      console.error('Exclude from print list failed:', err);
      alert('Could not update print list: ' + (err.message || String(err)));
    }
  };

  const handleRestoreExcludedLine = async (lineId) => {
    if (!lineId) return;
    try {
      const exS = [...excludedScreenPrint];
      const exDtg = [...excludedDTG];
      const exDtf = [...excludedDTF];
      const exEmb = [...excludedEmbroidery];
      let newScreen = lockedScreenPrintToday;
      let newDtg = lockedDTGToday;
      let newDtf = lockedDTFToday;
      let newEmb = lockedEmbroideryToday;

      if (activeTab === 'DTG') {
        const nextEx = exDtg.filter((id) => id !== lineId);
        newDtg = fillMinutes(dtgJobsAll.filter((j) => !nextEx.includes(j.id)));
        setExcludedDTG(nextEx);
        setLockedDTGToday(newDtg);
        await lockPrintList(newScreen, newDtg, newDtf, newEmb, {
          excludedScreenPrint: exS,
          excludedDTG: nextEx,
          excludedDTF: exDtf,
          excludedEmbroidery: exEmb
        });
        return;
      }
      if (activeTab === 'DTF') {
        const nextEx = exDtf.filter((id) => id !== lineId);
        newDtf = fillMinutes(dtfJobsAll.filter((j) => !nextEx.includes(j.id)));
        setExcludedDTF(nextEx);
        setLockedDTFToday(newDtf);
        await lockPrintList(newScreen, newDtg, newDtf, newEmb, {
          excludedScreenPrint: exS,
          excludedDTG: exDtg,
          excludedDTF: nextEx,
          excludedEmbroidery: exEmb
        });
        return;
      }
      if (activeTab === 'Embroidery') {
        const nextEx = exEmb.filter((id) => id !== lineId);
        newEmb = fillMinutes(embroideryJobsAll.filter((j) => !nextEx.includes(j.id)));
        setExcludedEmbroidery(nextEx);
        setLockedEmbroideryToday(newEmb);
        await lockPrintList(newScreen, newDtg, newDtf, newEmb, {
          excludedScreenPrint: exS,
          excludedDTG: exDtg,
          excludedDTF: exDtf,
          excludedEmbroidery: nextEx
        });
        return;
      }
      const nextExS = exS.filter((id) => id !== lineId);
      newScreen = fillMinutes(screenPrintJobsAll.filter((j) => !nextExS.includes(j.id)));
      setExcludedScreenPrint(nextExS);
      setLockedScreenPrintToday(newScreen);
      await lockPrintList(newScreen, newDtg, newDtf, newEmb, {
        excludedScreenPrint: nextExS,
        excludedDTG: exDtg,
        excludedDTF: exDtf,
        excludedEmbroidery: exEmb
      });
    } catch (err) {
      console.error('Restore excluded line failed:', err);
      alert('Could not update print list: ' + (err.message || String(err)));
    }
  };

  // Lock today's lists at 9:30 AM or on recalc
  useEffect(() => {
    const lockListsIfNeeded = async () => {
      const now = new Date();
      const isWeekday = !isWeekend(now);
      const isAfterLock = now.getHours() > 9 || (now.getHours() === 9 && now.getMinutes() >= 30);
      
      if (!isWeekday || !isAfterLock) return;
      
      // Check if we need to lock any lists
      const needsLocking = 
        (!screenPrintLockedAt && screenPrintJobsAll.length > 0) ||
        (!dtgLockedAt && dtgJobsAll.length > 0) ||
        (!dtfLockedAt && dtfJobsAll.length > 0) ||
        (!embroideryLockedAt && embroideryJobsAll.length > 0);
      
      if (!needsLocking) return;
      
      // Lock the lists that need locking
      const newScreenPrintJobs = !screenPrintLockedAt && screenPrintJobsAll.length > 0
        ? fillMinutes(screenPrintJobsAll.filter((j) => !excludedScreenPrint.includes(j.id)))
        : lockedScreenPrintToday;
      
      const newDTGJobs = !dtgLockedAt && dtgJobsAll.length > 0
        ? fillMinutes(dtgJobsAll.filter((j) => !excludedDTG.includes(j.id)))
        : lockedDTGToday;
      
      const newDTFJobs = !dtfLockedAt && dtfJobsAll.length > 0
        ? fillMinutes(dtfJobsAll.filter((j) => !excludedDTF.includes(j.id)))
        : lockedDTFToday;
      
      const newEmbroideryJobs = !embroideryLockedAt && embroideryJobsAll.length > 0
        ? fillMinutes(embroideryJobsAll.filter((j) => !excludedEmbroidery.includes(j.id)))
        : lockedEmbroideryToday;
      
      // Update state
      if (!screenPrintLockedAt && screenPrintJobsAll.length > 0) {
        setLockedScreenPrintToday(newScreenPrintJobs);
        setScreenPrintLockedAt(now);
      }
      if (!dtgLockedAt && dtgJobsAll.length > 0) {
        setLockedDTGToday(newDTGJobs);
        setDTGLockedAt(now);
      }
      if (!dtfLockedAt && dtfJobsAll.length > 0) {
        setLockedDTFToday(newDTFJobs);
        setDTFLockedAt(now);
      }
      if (!embroideryLockedAt && embroideryJobsAll.length > 0) {
        setLockedEmbroideryToday(newEmbroideryJobs);
        setEmbroideryLockedAt(now);
      }
      
      // Save to Firestore
      try {
        await lockPrintList(newScreenPrintJobs, newDTGJobs, newDTFJobs, newEmbroideryJobs, {
          excludedScreenPrint,
          excludedDTG,
          excludedDTF,
          excludedEmbroidery
        });
        console.log('Auto-locked print lists at 9:30 AM and saved to Firestore');
      } catch (error) {
        console.error('Error saving auto-locked print lists to Firestore:', error);
      }
    };
    
    lockListsIfNeeded();
  }, [screenPrintJobsAll, dtgJobsAll, dtfJobsAll, embroideryJobsAll, screenPrintLockedAt, dtgLockedAt, dtfLockedAt, embroideryLockedAt, lockedScreenPrintToday, lockedDTGToday, lockedDTFToday, lockedEmbroideryToday, excludedScreenPrint, excludedDTG, excludedDTF, excludedEmbroidery]);

  useEffect(() => {
    if (
      (activeTab === 'DTG' && lockedDTGToday.length === 0) ||
      (activeTab === 'DTF' && lockedDTFToday.length === 0) ||
      (activeTab === 'Screen Print' && lockedScreenPrintToday.length === 0)
    ) {
      setShowSuccessAnimation(true);
    } else {
      setShowSuccessAnimation(false);
    }
  }, [activeTab, lockedDTGToday, lockedDTFToday, lockedScreenPrintToday]);

  const handleRecalcScreenClick = () => setRecalcScreenDialogOpen(true);
  const handleRecalcScreenConfirm = async () => {
    const newScreenPrintJobs = fillMinutes(
      screenPrintJobsAll.filter((j) => !excludedScreenPrint.includes(j.id))
    );
    setLockedScreenPrintToday(newScreenPrintJobs);
    setScreenPrintLockedAt(new Date());
    await lockPrintList(newScreenPrintJobs, lockedDTGToday, lockedDTFToday, lockedEmbroideryToday, {
      excludedScreenPrint,
      excludedDTG,
      excludedDTF,
      excludedEmbroidery
    });
    setRecalcScreenDialogOpen(false);
  };
  const handleRecalcScreenCancel = () => setRecalcScreenDialogOpen(false);

  const handleRecalcDTGClick = () => setRecalcDTGDialogOpen(true);
  const handleRecalcDTGConfirm = async () => {
    const newDTGJobs = fillMinutes(dtgJobsAll.filter((j) => !excludedDTG.includes(j.id)));
    setLockedDTGToday(newDTGJobs);
    setDTGLockedAt(new Date());
    await lockPrintList(lockedScreenPrintToday, newDTGJobs, lockedDTFToday, lockedEmbroideryToday, {
      excludedScreenPrint,
      excludedDTG,
      excludedDTF,
      excludedEmbroidery
    });
    setRecalcDTGDialogOpen(false);
  };
  const handleRecalcDTGCancel = () => setRecalcDTGDialogOpen(false);

  const handleRecalcDTFClick = () => setRecalcDTFDialogOpen(true);
  const handleRecalcDTFConfirm = async () => {
    const newDTFJobs = fillMinutes(dtfJobsAll.filter((j) => !excludedDTF.includes(j.id)));
    setLockedDTFToday(newDTFJobs);
    setDTFLockedAt(new Date());
    await lockPrintList(lockedScreenPrintToday, lockedDTGToday, newDTFJobs, lockedEmbroideryToday, {
      excludedScreenPrint,
      excludedDTG,
      excludedDTF,
      excludedEmbroidery
    });
    setRecalcDTFDialogOpen(false);
  };
  const handleRecalcDTFCancel = () => setRecalcDTFDialogOpen(false);

  // Update the lock functions to persist to Firestore
  const lockPrintList = async (screenPrintJobs, dtgJobs, dtfJobs = [], embroideryJobs = [], exclusionOpts) => {
    try {
      const exS = exclusionOpts?.excludedScreenPrint ?? excludedScreenPrint;
      const exDtg = exclusionOpts?.excludedDTG ?? excludedDTG;
      const exDtf = exclusionOpts?.excludedDTF ?? excludedDTF;
      const exEmb = exclusionOpts?.excludedEmbroidery ?? excludedEmbroidery;

      const today = startOfDay(new Date());
      const lockedListRef = doc(
        db,
        'lockedPrintLists',
        facilityProgressDocId(facilityId, today.toISOString().split('T')[0])
      );
      
      // Clean the data before saving - preserve all fields needed for PrintLineModal
      const cleanJob = (job) => {
        if (!job) return null;
        return {
          id: job.id || '',
          orderId: job.orderId || '',
          orderNumber: job.orderNumber || 'N/A',
          designName: job.designName || 'Unnamed Design',
          totalQuantity: job.totalQuantity || 0,
          frontInkColors: Array.isArray(job.frontInkColors) ? job.frontInkColors : [],
          backInkColors: Array.isArray(job.backInkColors) ? job.backInkColors : [],
          otherInkColors: Array.isArray(job.otherInkColors) ? job.otherInkColors : [],
          dueDate: job.dueDate || null,
          estimatedTime: job.estimatedTime || 0,
          printed: Boolean(job.printed),
          // Additional fields for PrintLineModal display
          type: job.type || '',
          garmentId: job.garmentId || job.style || '',
          garmentColor: job.garmentColor || job.color || '',
          sizes: job.sizes || {},
          customerName: job.customerName || '',
          projectManager: job.projectManager || 'N/A',
          printLocations: Array.isArray(job.printLocations) ? job.printLocations : [],
          // Ink Notes for special printer instructions
          inkNotes: job.inkNotes || null,
          // Mockup/artwork URLs
          designFileUrl: job.designFileUrl || null,
          mockupUrl: job.mockupUrl || null,
          artworkUrl: job.artworkUrl || null,
          files: job.files ? {
            mockup: job.files.mockup ? { url: job.files.mockup.url, fileName: job.files.mockup.fileName } : null,
            production: Array.isArray(job.files.production) ? job.files.production : []
          } : null
        };
      };

      const cleanScreenPrintJobs = (screenPrintJobs || []).map(cleanJob).filter(Boolean);
      const cleanDTGJobs = (dtgJobs || []).map(cleanJob).filter(Boolean);
      const cleanDTFJobs = (dtfJobs || []).map(cleanJob).filter(Boolean);

      const cleanEmbroideryJobs = (embroideryJobs || []).map(job => {
        if (!job) return null;
        const baseClean = cleanJob(job);
        return {
          ...baseClean,
          // Embroidery uses thread colors instead of ink colors
          frontThreadColors: Array.isArray(job.frontThreadColors) ? job.frontThreadColors : [],
          backThreadColors: Array.isArray(job.backThreadColors) ? job.backThreadColors : [],
          otherThreadColors: Array.isArray(job.otherThreadColors) ? job.otherThreadColors : []
        };
      }).filter(Boolean);

      const dataToSave = {
        screenPrint: cleanScreenPrintJobs,
        dtg: cleanDTGJobs,
        dtf: cleanDTFJobs,
        embroidery: cleanEmbroideryJobs,
        excludedScreenPrint: Array.isArray(exS) ? exS : [],
        excludedDTG: Array.isArray(exDtg) ? exDtg : [],
        excludedDTF: Array.isArray(exDtf) ? exDtf : [],
        excludedEmbroidery: Array.isArray(exEmb) ? exEmb : [],
        screenPrintLockedAt: serverTimestamp(),
        dtgLockedAt: serverTimestamp(),
        dtfLockedAt: serverTimestamp(),
        embroideryLockedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // Debug log
      console.log('Data being saved:', JSON.stringify(dataToSave, null, 2));
      
      await setDoc(lockedListRef, dataToSave);
    } catch (error) {
      console.error('Error in lockPrintList:', error);
      console.error('Screen Print Jobs:', screenPrintJobs);
      console.error('DTG Jobs:', dtgJobs);
      throw error;
    }
  };

  const dtgTomorrowPreview = suggestedTomorrowFromPool(lockedDTGToday, dtgJobsAll, excludedDTG);
  const dtfTomorrowPreview = suggestedTomorrowFromPool(lockedDTFToday, dtfJobsAll, excludedDTF);
  const screenTomorrowPreview = suggestedTomorrowFromPool(
    lockedScreenPrintToday,
    screenPrintJobsAll,
    excludedScreenPrint
  );

  const renderExcludedTodaySection = () => {
    const { ids, pool } =
      activeTab === 'DTG'
        ? { ids: excludedDTG, pool: dtgJobsAll }
        : activeTab === 'DTF'
          ? { ids: excludedDTF, pool: dtfJobsAll }
          : { ids: excludedScreenPrint, pool: screenPrintJobsAll };
    if (!ids.length) return null;
    return (
      <Box
        sx={{
          mt: 2,
          p: 2,
          borderRadius: 1,
          border: '1px solid rgba(255, 183, 77, 0.45)',
          backgroundColor: 'rgba(255, 183, 77, 0.06)'
        }}
      >
        <Typography variant="subtitle2" sx={{ color: '#ffb74d', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Excluded from today&apos;s schedule ({ids.length})
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255,255,255,0.55)',
            display: 'block',
            mb: 1.5,
            textTransform: 'uppercase',
            letterSpacing: '0.02em',
            lineHeight: 1.45
          }}
        >
          Not ready to print today — minutes are backfilled from the queue. Excluded jobs stay out for the rest of the day (including Suggested Tomorrow) until you restore them or a new day. Recalculate refills today while keeping exclusions.
        </Typography>
        {ids.map((id) => {
          const meta = pool.find((j) => j.id === id) || printList.find((l) => l.id === id);
          return (
            <Box key={id} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, py: 0.5 }}>
              <Typography variant="body2" component="span" sx={{ textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                {meta?.designName || 'Unknown'}{' '}
                <Typography component="span" variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                  ({meta?.orderNumber || id})
                </Typography>
              </Typography>
              <Button
                size="small"
                variant="text"
                sx={{ color: 'var(--al-primary)', textTransform: 'uppercase', fontSize: '0.7rem' }}
                onClick={() => handleRestoreExcludedLine(id)}
              >
                Restore to schedule
              </Button>
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ 
        width: '100%', 
        padding: {
          xs: '60px 12px 12px 12px',
          md: '80px 20px 20px 20px'
        },
        backgroundColor: 'var(--al-bg)',
        minHeight: '100vh'
      }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress sx={{ color: 'var(--al-primary)' }} />
            <Typography ml={2} sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Loading print list...
            </Typography>
          </Box>
        ) : error ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Typography color="error" sx={{ textTransform: 'uppercase', letterSpacing: '0.03em' }}>
              Error loading print list: {error}
            </Typography>
          </Box>
        ) : (
          <>
            <SummaryBox>
              <Typography
                variant="h6"
                sx={{
                  color: 'var(--al-primary)',
                  mb: { xs: 1.25, md: 2 },
                  textTransform: 'uppercase',
                  fontSize: { xs: '0.85rem', md: '1.25rem' },
                  letterSpacing: '0.06em'
                }}
              >
                Today's Summary
              </Typography>
              <SummaryGrid>
                <SummaryItem>
                  <SummaryLabel>Total Jobs</SummaryLabel>
                  <SummaryValue>
                    {lockedScreenPrintToday.length + lockedDTGToday.length + lockedDTFToday.length + lockedEmbroideryToday.length}
                  </SummaryValue>
                </SummaryItem>
                <SummaryItem>
                  <SummaryLabel>Total Pieces</SummaryLabel>
                  <SummaryValue>
                    {lockedScreenPrintToday.reduce((sum, job) => sum + (job.totalQuantity || 0), 0) +
                     lockedDTGToday.reduce((sum, job) => sum + (job.totalQuantity || 0), 0) +
                     lockedDTFToday.reduce((sum, job) => sum + (job.totalQuantity || 0), 0) +
                     lockedEmbroideryToday.reduce((sum, job) => sum + (job.totalQuantity || 0), 0)}
                  </SummaryValue>
                </SummaryItem>
                <SummaryItem>
                  <SummaryLabel>Est Screen Print</SummaryLabel>
                  <SummaryValue>
                    {Math.ceil(lockedScreenPrintToday.reduce((sum, job) => sum + (job.estimatedTime || 0), 0) / 60)}h
                  </SummaryValue>
                </SummaryItem>
                <SummaryItem>
                  <SummaryLabel>Est DTG</SummaryLabel>
                  <SummaryValue>
                    {Math.ceil(lockedDTGToday.reduce((sum, job) => sum + (job.estimatedTime || 0), 0) / 60)}h
                  </SummaryValue>
                </SummaryItem>
                <SummaryItem>
                  <SummaryLabel>Est DTF</SummaryLabel>
                  <SummaryValue>
                    {Math.ceil(lockedDTFToday.reduce((sum, job) => sum + (job.estimatedTime || 0), 0) / 60)}h
                  </SummaryValue>
                </SummaryItem>
              </SummaryGrid>
            </SummaryBox>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mx: { xs: -0.5, md: 0 } }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  minHeight: 44,
                  '& .MuiTab-root': { minHeight: 44 }
                }}
              >
                <Tab
                  value="Screen Print"
                  label={<PrintListTabLabel text="Screen Print" count={lockedScreenPrintToday.length} />}
                />
                <Tab value="DTG" label={<PrintListTabLabel text="DTG" count={lockedDTGToday.length} />} />
                <Tab value="DTF" label={<PrintListTabLabel text="DTF" count={lockedDTFToday.length} />} />
              </Tabs>
            </Box>
            <Box sx={{ mt: 2 }}>
              {activeTab === 'DTG' ? (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleRecalcDTGClick}
                    sx={{ mb: 2, width: { xs: '100%', md: 'auto' }, py: { xs: 1.25, md: 1 } }}
                  >
                    Recalculate DTG
                  </Button>
                  <Dialog open={recalcDTGDialogOpen} onClose={handleRecalcDTGCancel}>
                    <DialogTitle>Recalculate Today's DTG Print List?</DialogTitle>
                    <DialogContent>
                      This will replace today&apos;s DTG schedule with a fresh fill from the latest queue. Jobs you excluded for today stay excluded; use Restore on each line if you want it back on the schedule.
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={handleRecalcDTGCancel}>Cancel</Button>
                      <Button onClick={handleRecalcDTGConfirm} color="primary" variant="contained">Recalculate</Button>
                    </DialogActions>
                  </Dialog>
                  <Box sx={SCHEDULE_PAIR_GRID_SX}>
                    <OrderTable
                      jobs={lockedDTGTodayForDisplay}
                      today={dtgToday}
                      title="DTG Print Schedule (Today)"
                      fallbackMessage="No DTG jobs for today."
                      handlePrintedChange={handlePrintedChange}
                      handleOpenLineDetail={handleOpenLineDetail}
                      onExcludeFromToday={handleExcludeFromToday}
                      locationColumnMode="dtg_dtf"
                      customerNameByLineId={customerNameByLineId}
                    />
                    <TomorrowBlock>
                      <OrderTable
                        jobs={dtgTomorrowPreview}
                        today={dtgToday}
                        title="Suggested Tomorrow"
                        fallbackMessage={
                          dtgJobsAll.length === 0
                            ? 'Hmmm.... Something will show up...'
                            : dtgTomorrowPreview.length === 0
                              ? 'No more jobs in the queue after today.'
                              : ''
                        }
                        handlePrintedChange={handlePrintedChange}
                        handleOpenLineDetail={handleOpenLineDetail}
                        locationColumnMode="dtg_dtf"
                        customerNameByLineId={customerNameByLineId}
                        scheduleColumnSpacer
                      />
                    </TomorrowBlock>
                  </Box>
                  {renderExcludedTodaySection()}
                </>
              ) : activeTab === 'DTF' ? (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleRecalcDTFClick}
                    sx={{ mb: 2, width: { xs: '100%', md: 'auto' }, py: { xs: 1.25, md: 1 } }}
                  >
                    Recalculate DTF
                  </Button>
                  <Dialog open={recalcDTFDialogOpen} onClose={handleRecalcDTFCancel}>
                    <DialogTitle>Recalculate Today's DTF Print List?</DialogTitle>
                    <DialogContent>
                      This will replace today&apos;s DTF schedule with a fresh fill from the queue. Excluded-for-today jobs stay excluded; use Restore to put one back on the schedule.
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={handleRecalcDTFCancel}>Cancel</Button>
                      <Button onClick={handleRecalcDTFConfirm} color="primary" variant="contained">Recalculate</Button>
                    </DialogActions>
                  </Dialog>
                  <Box sx={SCHEDULE_PAIR_GRID_SX}>
                    <OrderTable
                      jobs={lockedDTFTodayForDisplay}
                      today={dtfToday}
                      title="DTF Print Schedule (Today)"
                      fallbackMessage="No DTF jobs for today."
                      handlePrintedChange={handlePrintedChange}
                      handleOpenLineDetail={handleOpenLineDetail}
                      onExcludeFromToday={handleExcludeFromToday}
                      locationColumnMode="dtg_dtf"
                      customerNameByLineId={customerNameByLineId}
                    />
                    <TomorrowBlock>
                      <OrderTable
                        jobs={dtfTomorrowPreview}
                        today={dtfToday}
                        title="Suggested Tomorrow"
                        fallbackMessage={
                          dtfJobsAll.length === 0
                            ? 'Hmmm.... Something will show up...'
                            : dtfTomorrowPreview.length === 0
                              ? 'No more jobs in the queue after today.'
                              : ''
                        }
                        handlePrintedChange={handlePrintedChange}
                        handleOpenLineDetail={handleOpenLineDetail}
                        locationColumnMode="dtg_dtf"
                        customerNameByLineId={customerNameByLineId}
                        scheduleColumnSpacer
                      />
                    </TomorrowBlock>
                  </Box>
                  {renderExcludedTodaySection()}
                </>
              ) : (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleRecalcScreenClick}
                    sx={{ mb: 2, width: { xs: '100%', md: 'auto' }, py: { xs: 1.25, md: 1 } }}
                  >
                    Recalculate ScreenPrint
                  </Button>
                  <Dialog open={recalcScreenDialogOpen} onClose={handleRecalcScreenCancel}>
                    <DialogTitle>Recalculate Today's Screen Print List?</DialogTitle>
                    <DialogContent>
                      This will replace today&apos;s screen print schedule with a fresh fill from the queue. Excluded-for-today jobs stay excluded; use Restore to put one back on the schedule.
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={handleRecalcScreenCancel}>Cancel</Button>
                      <Button onClick={handleRecalcScreenConfirm} color="primary" variant="contained">Recalculate</Button>
                    </DialogActions>
                  </Dialog>
                  <Box sx={SCHEDULE_PAIR_GRID_SX}>
                    <OrderTable
                      jobs={lockedScreenPrintToday}
                      today={screenToday}
                      title="Screen Print Schedule (Today)"
                      fallbackMessage="No Screen Print jobs for today."
                      handlePrintedChange={handlePrintedChange}
                      handleOpenLineDetail={handleOpenLineDetail}
                      onExcludeFromToday={handleExcludeFromToday}
                      customerNameByLineId={customerNameByLineId}
                    />
                    <TomorrowBlock>
                      <OrderTable
                        jobs={screenTomorrowPreview}
                        today={screenToday}
                        title="Suggested Tomorrow"
                        fallbackMessage={
                          screenPrintJobsAll.length === 0
                            ? 'Hmmm.... Something will show up...'
                            : screenTomorrowPreview.length === 0
                              ? 'No more jobs in the queue after today.'
                              : ''
                        }
                        handlePrintedChange={handlePrintedChange}
                        handleOpenLineDetail={handleOpenLineDetail}
                        customerNameByLineId={customerNameByLineId}
                        scheduleColumnSpacer
                      />
                    </TomorrowBlock>
                  </Box>
                  {renderExcludedTodaySection()}
                </>
              )}
            </Box>
          </>
        )}
      </Box>
      
      {lineDetailOpen && lineDetailData && (
        <PrintLineModal
          line={lineDetailData}
          orderNumber={lineDetailData.orderNumber}
          dueDate={lineDetailData.dueDate}
          onClose={() => {
            setLineDetailOpen(false);
            setLineDetailData(null);
          }}
        />
      )}
      {showQCModal && (
        <QCPrintedModal
          line={selectedLine}
          onClose={() => setShowQCModal(false)}
          onComplete={handleQCComplete}
        />
      )}

      {boxDialogOpen && boxPrintOrder && (
        <BoxCountDialog
          open={boxDialogOpen}
          onClose={() => {
            setBoxDialogOpen(false);
            setBoxPrintOrder(null);
            boxPrintOrderRef.current = null;
          }}
          onConfirm={handlePrintBoxesFromPrintList}
          orderDetails={{
            orderNumber: boxPrintOrder.orderNumber || boxPrintOrder.poNumber,
            client: boxPrintOrder.customerName,
            lines: boxPrintOrder.lines
          }}
        />
      )}

      {boxToPrint && (
        <div style={{ display: 'none' }}>
          <BoxSticker
            invoiceNumber={boxToPrint.invoiceNumber}
            client={boxToPrint.client}
            total={boxToPrint.totalStr}
            manager={boxToPrint.manager}
            pieces={boxToPrint.pieces}
            currentBox={boxToPrint.currentBox}
            totalBoxes={boxToPrint.totalBoxes}
            onPdfGenerated={async (pdfData) => {
              if (!pdfData) {
                if (boxToPrint?.onComplete) boxToPrint.onComplete(false);
                return;
              }
              let printedSuccessfully = false;
              try {
                await printLabelBase64(pdfData, { type: 'box' });
                printedSuccessfully = true;
              } catch (err) {
                console.error('Box label print error:', err);
                alert(`Failed to print box label: ${err.message}`);
              } finally {
                if (boxToPrint?.onComplete) boxToPrint.onComplete(printedSuccessfully);
              }
            }}
          />
        </div>
      )}

      {currentBoxPrinting && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            backgroundColor: 'var(--al-surface)',
            color: 'white',
            padding: '12px 20px',
            zIndex: 10000,
            border: '1px solid var(--al-primary)',
            fontFamily: "'Montserrat', sans-serif",
            fontSize: '0.85rem'
          }}
        >
          Printing box {currentBoxPrinting.currentBox} of {currentBoxPrinting.totalBoxes}…
        </Box>
      )}
    </ThemeProvider>
  );
};

export default PrintList; 